<template>
  <div class="font-sans bg-[#f6f4f1] text-[#1f2937] min-h-screen flex flex-col">
    <!-- 🌟 HEADER -->
    <header class="bg-[#1a1a1a] text-white shadow sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
        <div class="flex items-center gap-2">
          <img src="/logo.png" alt="Logo" class="h-10 w-auto" />
          <span class="font-bold text-lg">GetLance</span>
        </div>

        <div class="flex gap-3">
          <router-link to="/register"
            class="px-4 py-2 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 transition">
            Criar conta
          </router-link>
        </div>
      </div>
    </header>

    <!-- 🔐 LOGIN -->
    <section class="flex-1 flex items-center justify-center px-6 py-16">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-[#e0dcd6]">

        <!-- TÍTULO CENTRALIZADO -->
        <div class="text-center mb-8">
          <h2 class="text-3xl font-bold text-[#152a34] mb-1">Bem-vindo de volta</h2>
          <p class="text-gray-500">Acesse sua conta para continuar.</p>
        </div>

        <!-- GOOGLE LOGIN -->
        <div class="mb-6">
          <div ref="googleBtn" class="w-full flex justify-center"></div>
        </div>

        <!-- SEPARADOR -->
        <div class="flex items-center my-6">
          <div class="flex-grow border-t border-gray-300"></div>
          <span class="mx-3 text-gray-500 text-sm">ou</span>
          <div class="flex-grow border-t border-gray-300"></div>
        </div>

        <!-- FORM -->
        <form @submit.prevent="login">

          <!-- EMAIL -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">E-mail</label>
            <input v-model="email" type="email" placeholder="seuemail@exemplo.com" class="input-gold" />
          </div>

          <!-- SENHA -->
          <div class="mb-4">
            <label class="block text-sm font-medium mb-1">Senha</label>

            <div class="relative">
              <input v-model="password" :type="passwordVisible ? 'text' : 'password'" placeholder="********"
                class="input-gold" />

              <button type="button" @click="passwordVisible = !passwordVisible"
                class="absolute inset-y-0 right-3 flex items-center text-gray-500">
                <i :class="passwordVisible ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
              </button>
            </div>
          </div>

          <!-- BOTÃO ENTRAR -->
          <button type="submit"
            class="w-full py-3 rounded-full bg-[#b99a52] hover:bg-[#a18446] text-white font-semibold transition"
            :disabled="loading">
            <span v-if="!loading">Entrar</span>
            <span v-else>Aguarde...</span>
          </button>
        </form>

        <!-- LINKS -->
        <div class="flex justify-between mt-6 text-sm">
          <button @click="goToForgotPassword" class="text-[#152a34] hover:underline">
            Esqueceu a senha?
          </button>

          <button @click="goToRegister" class="text-[#b99a52] font-semibold hover:underline">
            Criar conta
          </button>
        </div>

        <!-- TOAST -->
        <transition name="fade">
          <div v-if="toast.show" class="mt-6 p-3 rounded-lg text-white text-center"
            :class="toast.color === 'error' ? 'bg-[#ef4444]' : 'bg-[#10b981]'">
            {{ toast.message }}
          </div>
        </transition>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Swal from "sweetalert2";
import AppFooter from "@/components/AppFooter.vue";

const email = ref("");
const password = ref("");
const passwordVisible = ref(false);
const loading = ref(false);

const toast = ref({
  show: false,
  message: "",
  color: "error",
});

const router = useRouter();
const route = useRoute();
const store = useStore();

const GOOGLE_CLIENT_ID =
  "708117913743-j1tmpf9b8endvjd3im363cniugltsvjd.apps.googleusercontent.com";

const googleBtn = ref(null);

const showToast = (message, color = "error") => {
  toast.value = { show: true, message, color };
  setTimeout(() => (toast.value.show = false), 3000);
};

const goToRegister = () => router.push("/cadastro");
const goToForgotPassword = () => router.push("/esqueceu-senha");

onMounted(async () => {
  await nextTick();

  try {
    await waitForGoogle(5000); // espera até 5s
  } catch {
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Google Sign-In não carregou a tempo. Recarregue a página.",
    });
    return;
  }

  if (!googleBtn.value) {
    Swal.fire({
      icon: "error",
      title: "Erro",
      text: "Container do botão Google não foi encontrado (ref googleBtn).",
    });
    return;
  }

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: handleGoogleCallback,
  });

  window.google.accounts.id.renderButton(googleBtn.value, {
    theme: "outline",
    size: "large",
    text: "signin_with",
    shape: "pill",
    width: 360,
    logo_alignment: "left",
  });
});

const waitForGoogle = (timeoutMs = 4000) => {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const tick = () => {
      if (window.google?.accounts?.id) return resolve(true);
      if (Date.now() - start >= timeoutMs) return reject(new Error("timeout"));
      setTimeout(tick, 50);
    };

    tick();
  });
};

const login = async () => {
  if (!email.value || !password.value) {
    Swal.fire({
      icon: "warning",
      title: "Atenção",
      text: "Preencha todos os campos.",
    });
    return;
  }

  loading.value = true;

  try {
    const response = await store.dispatch("postLogin", {
      email: email.value,
      password: password.value,
    });

    if (!response.user || !response.token)
      return showToast("Falha ao recuperar sessão.");

    const redirect = route.query.redirect
      ? route.query.redirect
      : (["user", ""].includes(response.user.role) ? "/projetos" : "/dashboard");

    router.push(redirect);
  } catch (error) {
    const code = error.response?.data?.code;
    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({
        icon: "warning",
        title: "Usuário não ativado",
        text: "Verifique seu e-mail para ativar sua conta.",
      });
      return;
    }
    Swal.fire({
      icon: "error",
      text: error.response?.data?.error || "Erro ao entrar.",
    });
  } finally {
    loading.value = false;
  }
};

const handleGoogleCallback = async (response) => {
  try {
    loading.value = true;

    const result = await store.dispatch("signInWithGoogle", {
      credential: response.credential,
    });

    if (result?.token) {
      router.push("/");
      return;
    }

    if (result?.success) {
      Swal.fire({
        icon: "success",
        title: "Cadastro realizado!",
        text: "Verifique seu e-mail para confirmar seu cadastro.",
      });

      setTimeout(() => router.push("/login"), 1800);
      return;
    }

    Swal.fire({
      icon: "error",
      title: "Erro ao entrar com Google.",
      text: "Resposta inválida do servidor. Tente novamente.",
    });

  } catch (error) {
    console.error("Erro ao autenticar com Google:", error);
    const code = error.response?.data?.code;

    if (code === "USER_NOT_ACTIVATED") {
      Swal.fire({
        icon: "warning",
        title: "Usuário não ativado",
        text: "Verifique seu e-mail para ativar sua conta.",
      });
      return;
    }

    Swal.fire({
      icon: "error",
      title: "Erro ao entrar com Google.",
      text: error.response?.data?.error || "Tente novamente mais tarde.",
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
